import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, KeyRound, Pencil, Search, ShieldCheck, Star, Trash2, UserPlus, X } from 'lucide-react';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { legalOperationsService } from '../../../services/legalOperationsService';

const emptyForm = {
  full_name: '',
  email: '',
  password: '',
  phone: '',
  role_id: 'lawyer',
  department: 'legal_operations',
  practice_area: 'labor',
};

const roleNames = {
  super_admin: 'Super Admin',
  legal_manager: 'مدير العمليات القانونية',
  lawyer: 'محامي',
  legal_secretary: 'سكرتير قانوني',
  reviewer: 'مراجع / مدقق',
  client: 'عميل',
};

const LegalOperationsUsersPage = () => {
  const { token } = useAdminAuth();
  const [data, setData] = useState({ users: [], roles: [] });
  const [metadata, setMetadata] = useState(null);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewForm, setReviewForm] = useState({ achievement: 5, speed: 5, quality: 5, client_handling: 5, commitment: 5, notes: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const [meta, users] = await Promise.all([
        legalOperationsService.getMetadata(token),
        legalOperationsService.getUsers(token),
      ]);
      setMetadata(meta);
      setData(users);
    } catch (loadError) {
      setError(loadError.message);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const canManage = metadata?.permissions?.includes('*') || metadata?.permissions?.includes('platform.manage_users');
  const filtered = useMemo(() => data.users.filter((user) => {
    const text = `${user.full_name || ''} ${user.email || ''}`.toLowerCase();
    return (!query || text.includes(query.toLowerCase())) && (roleFilter === 'all' || user.operations_role === roleFilter);
  }), [data.users, query, roleFilter]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({
      full_name: user.full_name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      role_id: user.operations_role || 'lawyer',
      department: user.department || 'legal_operations',
      practice_area: user.practice_area || 'labor',
    });
    setShowForm(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');
    try {
      if (editing) {
        await legalOperationsService.updateUser(token, editing.id, {
          full_name: form.full_name,
          phone: form.phone,
          department: form.department,
          practice_area: form.practice_area,
        });
        if (form.role_id !== editing.operations_role) {
          await legalOperationsService.assignRole(token, editing.id, form.role_id);
        }
        setMessage('تم تحديث المستخدم وصلاحياته.');
      } else {
        await legalOperationsService.createUser(token, form);
        setMessage('تم إنشاء المستخدم وتعيين دوره.');
      }
      setShowForm(false);
      await load();
    } catch (submitError) {
      setError(submitError.message);
    }
  };

  const toggleActive = async (user) => {
    try {
      await legalOperationsService.updateUser(token, user.id, { is_active: !user.is_active });
      setMessage(user.is_active ? 'تم تعطيل المستخدم.' : 'تم تفعيل المستخدم.');
      await load();
    } catch (toggleError) {
      setError(toggleError.message);
    }
  };

  const deleteUser = async (user) => {
    if (user.operations_role === 'super_admin') {
      setError('لا يمكن حذف المدير العام.');
      return;
    }
    const confirmed = window.confirm('هل أنت متأكد من حذف الموظف؟ لا يمكن التراجع عن هذا الإجراء.');
    if (!confirmed) return;
    try {
      await legalOperationsService.deleteUser(token, user.id);
      setMessage('تم حذف الموظف.');
      await load();
    } catch (deleteError) {
      setError(deleteError.message);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    try {
      await legalOperationsService.resetUserPassword(token, passwordTarget.id, newPassword);
      setPasswordTarget(null);
      setNewPassword('');
      setMessage('تم تغيير كلمة المرور.');
    } catch (passwordError) {
      setError(passwordError.message);
    }
  };

  const submitReview = async (event) => {
    event.preventDefault();
    try {
      await legalOperationsService.createPerformanceReview(token, reviewTarget.id, reviewForm);
      setReviewTarget(null);
      setReviewForm({ achievement: 5, speed: 5, quality: 5, client_handling: 5, commitment: 5, notes: '' });
      setMessage('تم حفظ تقييم الأداء.');
      await load();
    } catch (reviewError) {
      setError(reviewError.message);
    }
  };

  return (
    <LaborOperationsLayout searchValue={query} onSearchChange={setQuery}>
      <main className="mx-auto max-w-[1500px] px-6 pb-14 pt-10 sm:px-12" dir="rtl">
        <section className="lo-hero px-1 pb-8 pt-2">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="lo-kicker font-bold">ACCESS CONTROL</p>
              <h1 className="lo-heading legal-title mt-4 font-bold">المستخدمون والصلاحيات</h1>
              <p className="lo-lead mt-3">إدارة مستخدمي المنصة القانونية وأدوارهم ومسارات عملهم.</p>
            </div>
            {canManage && (
              <button type="button" onClick={openNew} className="inline-flex h-13 items-center gap-2 rounded-xl bg-[#293a50] px-6 py-4 text-[15px] font-bold text-white shadow-[0_6px_14px_rgba(41,58,80,0.10)]">
                <UserPlus className="h-4 w-4" /> إضافة مستخدم
              </button>
            )}
          </div>
          {message && <p className="mt-5 rounded-xl bg-[#eaf3ef] px-4 py-3 text-sm font-bold text-[#486e5c]">{message}</p>}
          {error && <p className="mt-5 rounded-xl border border-[#ecdacb] bg-[#faf1e8] px-4 py-3 text-sm font-bold text-[#99623b]">{error}</p>}
        </section>

        <section className="lo-surface grid overflow-hidden rounded-[18px] sm:grid-cols-3">
          <div className="border-b border-[#e7e2d9] px-7 py-6 sm:border-b-0 sm:border-l"><p className="text-[14px] text-[#697483]">المستخدمون</p><p className="mt-2 text-[36px] font-bold text-[#293a50]">{data.users.length}</p></div>
          <div className="border-b border-[#e7e2d9] px-7 py-6 sm:border-b-0 sm:border-l"><p className="text-[14px] text-[#697483]">نشطون</p><p className="mt-2 text-[36px] font-bold text-[#293a50]">{data.users.filter((user) => user.is_active !== false).length}</p></div>
          <div className="px-7 py-6"><p className="text-[14px] text-[#697483]">الأدوار النظامية</p><p className="mt-2 text-[36px] font-bold text-[#293a50]">{data.roles.length}</p></div>
        </section>

        <section className="lo-surface mt-10 overflow-hidden rounded-[18px]">
          <div className="flex flex-wrap gap-3 border-b border-[#e7e2d9] p-6">
            <label className="lo-control flex h-12 min-w-[250px] flex-1 items-center gap-2 rounded-xl px-4">
              <Search className="h-4 w-4 text-[#948066]" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="بحث بالاسم أو البريد" className="flex-1 bg-transparent text-[15px] outline-none" />
            </label>
            <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} className="lo-control h-12 rounded-xl px-5 font-semibold">
              <option value="all">كل الأدوار</option>
              {data.roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="lo-table min-w-[850px] w-full">
              <thead className="text-right">
                <tr>{['المستخدم', 'الدور', 'القسم / المسار', 'الحالة', 'الإجراء'].map((header) => <th key={header} className="border-b px-7 py-5">{header}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map((user) => (
                  <tr key={user.id} className="border-b transition">
                    <td className="px-7 py-5"><p className="font-bold text-[#293a50]">{user.full_name}</p><p className="mt-1 text-[14px] text-[#697483]">{user.email}</p></td>
                    <td className="px-7 py-5"><span className="rounded-lg bg-[#eee8dd] px-3 py-1.5 text-[13px] font-bold text-[#806f58]">{roleNames[user.operations_role] || user.operations_role}</span></td>
                    <td className="px-7 py-5 text-[14px] text-[#697483]">{user.department || '-'} / {user.practice_area || '-'}</td>
                    <td className="px-7 py-5"><span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-bold ${user.is_active === false ? 'bg-[#f6eded] text-[#876060]' : 'bg-[#eaf3ef] text-[#486e5c]'}`}><CheckCircle2 className="h-3.5 w-3.5" />{user.is_active === false ? 'معطل' : 'نشط'}</span></td>
                    <td className="px-7 py-5"><div className="flex gap-2">
                      {canManage && <button type="button" onClick={() => openEdit(user)} className="rounded-lg bg-[#f0eee8] p-2.5 text-[#697483]"><Pencil className="h-4 w-4" /></button>}
                      {canManage && <button type="button" onClick={() => toggleActive(user)} className="rounded-lg bg-[#f0eee8] px-3 py-2 text-[13px] font-bold text-[#697483]">{user.is_active === false ? 'تفعيل' : 'تعطيل'}</button>}
                      {canManage && <button type="button" onClick={() => { setPasswordTarget(user); setNewPassword(''); }} className="rounded-lg bg-[#f0eee8] p-2.5 text-[#697483]" title="تغيير كلمة المرور"><KeyRound className="h-4 w-4" /></button>}
                      {canManage && <button type="button" onClick={() => setReviewTarget(user)} className="rounded-lg bg-[#f0eee8] p-2.5 text-[#697483]" title="تقييم الأداء"><Star className="h-4 w-4" /></button>}
                      {canManage && user.operations_role !== 'super_admin' && <button type="button" onClick={() => deleteUser(user)} className="rounded-lg bg-[#f8eeee] p-2.5 text-[#896264]" title="حذف الموظف"><Trash2 className="h-4 w-4" /></button>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && <p className="p-12 text-center text-sm text-[#8b94a0]">لا يوجد مستخدمون مطابقون.</p>}
          </div>
        </section>

        <section className="lo-surface mt-10 rounded-[18px] p-7">
          <div className="mb-7 flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-[#948066]" />
            <h2 className="legal-title text-[21px] font-bold text-[#293a50]">الأدوار والصلاحيات الممنوحة</h2>
          </div>
          <div className="grid gap-x-8 gap-y-0 md:grid-cols-2 xl:grid-cols-3">
            {data.roles.map((role) => (
              <article key={role.id} className="border-b border-[#ece8e0] px-1 py-5">
                <p className="text-[16px] font-bold text-[#293a50]">{role.name}</p>
                <p className="mt-2 min-h-[42px] text-[14px] leading-7 text-[#697483]">{role.description}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(role.permissions || []).slice(0, 5).map((permission) => (
                    <span key={permission} className="rounded-md bg-[#f0eee8] px-2.5 py-1 text-[12px] font-medium text-[#5f6a78]">{permission === '*' ? 'كامل الصلاحيات' : permission}</span>
                  ))}
                  {(role.permissions || []).length > 5 && <span className="rounded-md bg-[#eee8dd] px-2.5 py-1 text-[12px] font-bold text-[#806f58]">+{role.permissions.length - 5}</span>}
                </div>
              </article>
            ))}
          </div>
        </section>

        {showForm && canManage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#273950]/25 p-5 backdrop-blur-sm">
            <form onSubmit={submit} className="lo-surface w-full max-w-[610px] rounded-[20px] p-7 shadow-[0_22px_62px_rgba(39,57,80,0.12)]">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#948066]" /><h2 className="legal-title text-xl font-bold text-[#293a50]">{editing ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'}</h2></div>
                <button type="button" onClick={() => setShowForm(false)}><X className="h-5 w-5 text-[#8b94a0]" /></button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input required value={form.full_name} onChange={(event) => setForm({ ...form, full_name: event.target.value })} placeholder="الاسم الكامل" className="h-12 rounded-xl border border-[#eadfce] bg-white px-4 text-sm outline-none" />
                <input required disabled={!!editing} type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="البريد الإلكتروني" className="h-12 rounded-xl border border-[#eadfce] bg-white px-4 text-sm outline-none disabled:opacity-55" />
                {!editing && <input required type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder="كلمة المرور" className="h-12 rounded-xl border border-[#eadfce] bg-white px-4 text-sm outline-none" />}
                <input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="رقم الجوال" className="h-12 rounded-xl border border-[#eadfce] bg-white px-4 text-sm outline-none" />
                <select value={form.role_id} onChange={(event) => setForm({ ...form, role_id: event.target.value })} className="h-12 rounded-xl border border-[#eadfce] bg-white px-4 text-sm font-bold text-[#273950]">
                  {data.roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
                </select>
                <input value={form.department} onChange={(event) => setForm({ ...form, department: event.target.value })} placeholder="القسم" className="h-12 rounded-xl border border-[#eadfce] bg-white px-4 text-sm outline-none" />
                <select value={form.practice_area} onChange={(event) => setForm({ ...form, practice_area: event.target.value })} className="h-12 rounded-xl border border-[#eadfce] bg-white px-4 text-sm font-bold text-[#273950]">
                  <option value="labor">القضايا العمالية</option><option value="commercial">القضايا التجارية</option><option value="execution">التنفيذ</option><option value="contracts">العقود</option>
                </select>
              </div>
              <div className="mt-7 flex justify-end gap-3">
                <button type="button" onClick={() => setShowForm(false)} className="rounded-xl border border-[#eadfce] px-5 py-3 text-sm font-bold text-[#667283]">إلغاء</button>
                <button type="submit" className="rounded-xl bg-[#33455d] px-6 py-3 text-sm font-bold text-white">حفظ المستخدم</button>
              </div>
            </form>
          </div>
        )}
        {passwordTarget && canManage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#273950]/25 p-5 backdrop-blur-sm">
            <form onSubmit={resetPassword} className="lo-surface w-full max-w-[460px] rounded-[20px] p-7 shadow-[0_22px_62px_rgba(39,57,80,0.12)]">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="legal-title text-xl font-bold text-[#293a50]">تغيير كلمة المرور</h2>
                <button type="button" onClick={() => setPasswordTarget(null)}><X className="h-5 w-5 text-[#8b94a0]" /></button>
              </div>
              <p className="mb-4 text-sm text-[#697483]">{passwordTarget.full_name}</p>
              <input required minLength={8} type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="كلمة مرور جديدة" className="h-12 w-full rounded-xl border border-[#eadfce] bg-white px-4 text-sm outline-none" />
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setPasswordTarget(null)} className="rounded-xl border border-[#eadfce] px-5 py-3 text-sm font-bold text-[#667283]">إلغاء</button>
                <button type="submit" className="rounded-xl bg-[#33455d] px-6 py-3 text-sm font-bold text-white">حفظ كلمة المرور</button>
              </div>
            </form>
          </div>
        )}
        {reviewTarget && canManage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#273950]/25 p-5 backdrop-blur-sm">
            <form onSubmit={submitReview} className="lo-surface w-full max-w-[680px] rounded-[20px] p-7 shadow-[0_22px_62px_rgba(39,57,80,0.12)]">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="legal-title text-xl font-bold text-[#293a50]">تقييم أداء الموظف</h2>
                <button type="button" onClick={() => setReviewTarget(null)}><X className="h-5 w-5 text-[#8b94a0]" /></button>
              </div>
              <p className="mb-5 text-sm font-bold text-[#697483]">{reviewTarget.full_name}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  ['achievement', 'الإنجاز'],
                  ['speed', 'سرعة الإنجاز'],
                  ['quality', 'جودة العمل'],
                  ['client_handling', 'التعامل مع العملاء'],
                  ['commitment', 'الالتزام بالمهام'],
                ].map(([key, label]) => (
                  <label key={key} className="text-sm font-bold text-[#293a50]">
                    {label}
                    <select value={reviewForm[key]} onChange={(event) => setReviewForm({ ...reviewForm, [key]: Number(event.target.value) })} className="mt-2 h-11 w-full rounded-xl border border-[#eadfce] bg-white px-3">
                      {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value} / 5</option>)}
                    </select>
                  </label>
                ))}
                <textarea value={reviewForm.notes} onChange={(event) => setReviewForm({ ...reviewForm, notes: event.target.value })} placeholder="ملاحظات الإدارة" rows={4} className="sm:col-span-2 rounded-xl border border-[#eadfce] bg-white px-4 py-3 text-sm outline-none" />
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setReviewTarget(null)} className="rounded-xl border border-[#eadfce] px-5 py-3 text-sm font-bold text-[#667283]">إلغاء</button>
                <button type="submit" className="rounded-xl bg-[#33455d] px-6 py-3 text-sm font-bold text-white">حفظ التقييم</button>
              </div>
            </form>
          </div>
        )}
      </main>
    </LaborOperationsLayout>
  );
};

export default LegalOperationsUsersPage;
